'use client'

import Link from 'next/link'
import { ScanSearch, ArrowRight, Stethoscope } from 'lucide-react'
import { trackEvent } from '@/lib/analytics'
import { isDiagnosisPost, DIAGNOSE_CTA } from '@/lib/blog-diagnose-cta'
import { IDENTIFY_CTA, identifyCtaProps } from '@/lib/identify-cta'
import { useImpressionEvent } from '@/hooks/useImpressionEvent'
import { analytics } from '@/lib/analytics'

interface StickySidebarCTAProps {
  slug?: string
  category?: string
  tags?: string[]
  /** Force the /diagnose variant regardless of keyword detection. For surfaces
   *  that are inherently a symptom/diagnosis context (e.g. weed guide pages)
   *  whose other CTAs already point at /diagnose. Kept separate from the blog
   *  keyword list so it does not change blog-post routing. */
  diagnoseContext?: boolean
}

/**
 * Sidebar conversion CTA. Defaults to the grass-ID flow, but symptom/problem
 * posts route to the higher-converting /diagnose flow (July 2026 audit) so the
 * sidebar matches the reader's intent — they have a symptom, not a "what grass
 * is this" question.
 */
export function StickySidebarCTA({ slug, category, tags, diagnoseContext }: StickySidebarCTAProps = {}) {
  const diagnose = diagnoseContext || isDiagnosisPost(slug, category, tags)

  const href = diagnose ? DIAGNOSE_CTA.href : '/#analysis-card'
  const ctaLocation = diagnose ? DIAGNOSE_CTA.location.sidebar : 'content_sidebar_identify_grass'
  const Icon = diagnose ? Stethoscope : ScanSearch
  const heading = diagnose ? 'Diagnose Your Lawn' : IDENTIFY_CTA.label
  const description = diagnose
    ? 'Snap a photo and get a likely cause with a step-by-step fix in seconds.'
    : 'Upload a photo and get an instant AI identification with a personalized care plan.'

  // Denominator. This surface logged clicks with no impressions since it shipped,
  // so its clicks pooled against other surfaces' impressions whenever a reader
  // computed a page-level CTR — which is how a 20.8% "best bridge on the site"
  // figure entered circulation (obsidian/research/funnel-decisions-2026-08.md).
  // Keyed on the SAME `ctaLocation` the click reports, so CTR joins per branch.
  const impressionRef = useImpressionEvent<HTMLAnchorElement>(
    ctaLocation,
    { surface: 'sticky_sidebar_cta', ...(slug ? { slug } : {}) },
    { emit: analytics.ctaSurfaceShown },
  )

  return (
    <Link
      ref={impressionRef}
      href={href}
      onClick={() => trackEvent('cta_clicked', diagnose
        ? { cta_location: ctaLocation, cta_text: 'Try it free' }
        // Passive-surface "Try it free" framing preserved; the helper adds the
        // destination fields so this identify CTA is instrumented too.
        : identifyCtaProps(IDENTIFY_CTA.location.sidebar, { text: 'Try it free' }))}
      className="group block p-5 rounded-[18px] bg-[rgba(10,28,18,0.34)] backdrop-blur-xl border border-white/20 transition-[transform,box-shadow] duration-300 hover:-translate-y-1"
      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.30)" }}
    >
      <div className="flex items-center gap-2.5 mb-2">
        <div className="grid place-items-center h-9 w-9 rounded-2xl bg-linear-to-r from-green-600 to-emerald-600 text-white shadow-[0_8px_20px_-8px_rgba(22,163,74,0.6)]">
          <Icon className="w-4 h-4" />
        </div>
        <span className="font-editorial text-white font-semibold text-base">
          {heading}
        </span>
      </div>
      <p className="text-white/75 text-xs leading-relaxed mb-3.5">
        {description}
      </p>
      <span className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-linear-to-r from-green-600 to-emerald-600 px-6 min-h-[44px] text-white text-sm font-semibold transition hover:-translate-y-0.5 shadow-[0_12px_26px_-8px_rgba(22,163,74,0.5)]">
        Try it free
        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  )
}
